<template>
  <modal
    :name="modalName"
    :width="'94%'"
    :height="'92%'"
    :max-width="1440"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="climbing-preview-modal modal-not-overflow"
    @opened="focusHeaderClose"
    @before-close="onBeforeClose"
  >
    <article class="climbing-preview" role="dialog" aria-modal="true" aria-labelledby="climbing-preview-title">
      <AppModalHeader
        ref="modalHeader"
        design="new"
        eyebrow="Climbing group preview"
        :title="title"
        title-id="climbing-preview-title"
        @close="close"
      />

      <div class="climbing-preview__toolbar">
        <nav class="climbing-preview__tabs" aria-label="Group preview sections">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="climbing-preview__tab"
            :class="{ 'climbing-preview__tab--active': activeTab === tab.key }"
            :aria-current="activeTab === tab.key ? 'page' : null"
            @click="selectTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </nav>
        <router-link :to="detailRoute" class="climbing-preview__detail-link">
          Open full details
        </router-link>
      </div>

      <div class="climbing-preview__body">
        <div v-if="current.loading" class="climbing-preview__state">
          <app-spinner design="new" />
        </div>
        <div v-else-if="current.error" class="climbing-preview__state climbing-preview__state--error">
          <strong>{{ current.error }}</strong>
          <app-button design="new" theme="dark" @click.native="load(activeTab, true)">Retry</app-button>
        </div>

        <div v-else-if="activeTab === 'overview' && current.data" class="climbing-preview__grid climbing-preview__grid--overview">
          <AppCard design="new" title="General information" subtitle="Key dates and operational details">
            <dl class="climbing-preview__facts">
              <div><dt>Dates</dt><dd>{{ dateRange(current.data.group) }}</dd></div>
              <div><dt>Route</dt><dd>{{ routeLabel(current.data.group.route) }}</dd></div>
              <div><dt>Group type</dt><dd><StatusPill :item="current.data.group.type" /></dd></div>
              <div><dt>Pax</dt><dd>{{ current.data.group.pax }}</dd></div>
              <div><dt>Meeting</dt><dd>{{ meetingLabel(current.data.group) }}</dd></div>
              <div><dt>Chief guide</dt><dd>{{ value(current.data.group.chief_guide && current.data.group.chief_guide.full_name) }}</dd></div>
              <div><dt>GPS</dt><dd>
                <a v-if="current.data.group.gps && current.data.group.gps.shared_link" :href="current.data.group.gps.shared_link" target="_blank" rel="noopener noreferrer">
                  {{ current.data.group.gps.name || current.data.group.gps.number || 'Open tracking' }}
                </a>
                <span v-else>—</span>
              </dd></div>
              <div><dt>Resupply</dt><dd>{{ formatDate(current.data.group.resupply) }}</dd></div>
              <div><dt>Summit</dt><dd>{{ formatDate(current.data.group.summit) }}</dd></div>
            </dl>
            <div v-if="current.data.group.notes_climbing_manager" class="climbing-preview__note">
              <strong>Climbing manager notes</strong>
              <p>{{ current.data.group.notes_climbing_manager }}</p>
            </div>
          </AppCard>

          <AppCard
            design="new"
            :title="`Clients · ${current.data.clients.length}`"
            subtitle="Clients assigned to this climbing group"
            padding="none"
          >
            <ItemList :items="current.data.clients" empty-text="No clients assigned">
              <template #default="{ item }">
                <div>
                  <strong>{{ item.full_name }}</strong>
                  <span>{{ [item.category, item.citizenship].filter(Boolean).join(' · ') || 'Client' }}</span>
                </div>
              </template>
            </ItemList>
          </AppCard>
        </div>

        <div v-else-if="activeTab === 'operations' && current.data" class="climbing-preview__grid">
          <AppCard design="new" :title="`Tents / huts · ${current.data.tents.length}`" padding="none">
            <ItemList :items="current.data.tents" empty-text="No tents or huts added">
              <template #default="{ item, index }">
                <div>
                  <strong>{{ item.service || 'Tent' }} {{ item.index || index + 1 }}</strong>
                  <span>{{ clientNames(item.clients) }}</span>
                </div>
              </template>
            </ItemList>
          </AppCard>
          <AppCard design="new" :title="`Additional services · ${current.data.additional_services.length}`" padding="none">
            <ItemList :items="current.data.additional_services" empty-text="No additional services added">
              <template #default="{ item }">
                <div>
                  <strong>{{ item.service }}<template v-if="item.index"> · #{{ item.index }}</template></strong>
                  <span>{{ clientNames(item.clients) }}<template v-if="item.ready_to_share"> · Ready to share</template></span>
                </div>
              </template>
            </ItemList>
          </AppCard>
        </div>

        <div v-else-if="activeTab === 'requests' && current.data" class="climbing-preview__grid">
          <AppCard design="new" :title="`Special requests · ${current.data.special_requests.length}`" padding="none">
            <ItemList :items="current.data.special_requests" empty-text="No special requests">
              <template #default="{ item }">
                <div>
                  <strong>{{ item.description }}</strong>
                  <span>{{ [formatDate(item.date), clientNames(item.clients)].filter(part => part !== '—').join(' · ') || 'All clients' }}</span>
                </div>
              </template>
            </ItemList>
          </AppCard>
          <AppCard design="new" :title="`Food · ${current.data.food.length}`" padding="none">
            <ItemList :items="current.data.food" empty-text="No clients">
              <template #default="{ item }">
                <div>
                  <strong>{{ item.full_name }}</strong>
                  <span>{{ foodLabel(item) }}</span>
                </div>
              </template>
            </ItemList>
          </AppCard>
        </div>
      </div>
    </article>
  </modal>
</template>

<script>
import { format, parse } from 'date-fns'
import { getClimbingGroupPreview } from '@/http'
import AppCard from '@/components/AppCard'
import AppModalHeader from '@/components/AppModalHeader'

const ItemList = {
  functional: true,
  props: {
    items: { type: Array, required: true },
    emptyText: { type: String, required: true },
  },
  render(h, { props, scopedSlots }) {
    if (!props.items.length) return h('p', { class: 'climbing-preview__empty' }, props.emptyText)
    return h('ul', { class: 'climbing-preview__list' }, props.items.map((item, index) => (
      h('li', { key: item.id || index }, [scopedSlots.default({ item, index })])
    )))
  },
}

const StatusPill = {
  functional: true,
  props: { item: { type: Object, default: null } },
  render(h, { props }) {
    if (!props.item) return h('span', '—')
    return h('span', {
      class: 'climbing-preview__pill',
      style: { backgroundColor: props.item.color || '#697386' },
    }, props.item.name)
  },
}

function sectionState() {
  return { loading: false, loaded: false, error: '', data: null, requestId: 0 }
}

export default {
  name: 'ClimbingGroupPreviewModal',
  components: { AppCard, AppModalHeader, ItemList, StatusPill },
  props: { groupId: { type: Number, required: true } },
  data() {
    return {
      modalName: 'climbing-group-preview',
      activeTab: 'overview',
      destroyed: false,
      tabs: [
        { key: 'overview', label: 'Overview & clients' },
        { key: 'operations', label: 'Tents & services' },
        { key: 'requests', label: 'Requests & food' },
      ],
      sections: {
        overview: sectionState(),
        operations: sectionState(),
        requests: sectionState(),
      },
    }
  },
  computed: {
    current() { return this.sections[this.activeTab] },
    title() {
      const overview = this.sections.overview.data
      return overview ? overview.group.name : `Group #${ this.groupId }`
    },
    detailRoute() {
      return { name: 'climbing-groups-detail-clients', params: { id: this.groupId } }
    },
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.load('overview')
  },
  beforeDestroy() {
    this.destroyed = true
    Object.values(this.sections).forEach(section => { section.requestId += 1 })
  },
  methods: {
    selectTab(key) {
      this.activeTab = key
      this.load(key)
    },
    async load(key, force = false) {
      const section = this.sections[key]
      if (section.loading || (section.loaded && !force)) return
      const requestId = ++section.requestId
      section.loading = true
      section.error = ''
      try {
        const { data } = await getClimbingGroupPreview(this.groupId, key)
        if (!this.destroyed && requestId === section.requestId) {
          section.data = data
          section.loaded = true
        }
      } catch (error) {
        if (!this.destroyed && requestId === section.requestId) section.error = this.errorMessage(error)
      } finally {
        if (!this.destroyed && requestId === section.requestId) section.loading = false
      }
    },
    close() { if (!this._isDestroyed) this.$emit('close') },
    onBeforeClose(event) { event.cancel(); this.close() },
    focusHeaderClose() {
      this.$nextTick(() => { if (this.$refs.modalHeader) this.$refs.modalHeader.focusClose() })
    },
    value(value) { return value || '—' },
    formatDate(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '—'
    },
    dateRange(group) { return `${ this.formatDate(group.tour_start) } — ${ this.formatDate(group.tour_end) }` },
    routeLabel(route) {
      if (!route) return '—'
      return [route.name, `${ route.duration } days`, route.accommodation_type].filter(Boolean).join(' · ')
    },
    meetingLabel(group) {
      return [group.meeting_time && group.meeting_time.slice(0, 5), group.starting_point && group.starting_point.short_name].filter(Boolean).join(' · ') || '—'
    },
    clientNames(clients) {
      return clients && clients.length ? clients.map(client => client.full_name).join(', ') : 'All clients'
    },
    foodLabel(item) {
      const flags = [
        item.preference,
        item.lactose_free && 'Lactose free',
        item.gluten_free && 'Gluten free',
        item.halal && 'Halal',
        item.no_red_meat && 'No red meat',
        item.no_chicken && 'No chicken',
        item.allergy && `Allergy: ${ item.allergy }`,
      ].filter(Boolean)
      return flags.join(' · ') || 'No food preferences'
    },
    errorMessage(error) {
      const data = error.response && error.response.data
      return (data && (data.detail || data.message)) || 'Unable to load this preview section.'
    },
  },
}
</script>

<style lang="scss">
.climbing-preview-modal .v--modal { overflow: hidden; background: $ui-surface-soft; }

.climbing-preview {
  display: flex;
  height: 100%;
  flex-direction: column;
  color: $ui-text;
  font-family: $ui-font-body;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $ui-space-16;
    padding: $ui-space-12 $ui-space-24;
    border-bottom: 1px solid $ui-line;
    background: $ui-surface;
  }

  &__tabs { display: flex; gap: $ui-space-8; overflow-x: auto; }

  &__tab {
    padding: $ui-space-8 $ui-space-12;
    border: 0;
    border-radius: $ui-control-radius;
    background: transparent;
    color: $ui-muted;
    font: inherit;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
  }

  &__tab:hover { background: $ui-surface-soft; color: $ui-ink; }
  &__tab--active { background: $ui-blue-soft; color: $ui-blue; }

  &__detail-link { color: $ui-blue; font-weight: 600; text-decoration: none; white-space: nowrap; }
  &__detail-link:hover { color: $ui-blue-hover; text-decoration: underline; }

  &__body { flex: 1; min-height: 0; padding: $ui-space-24; overflow-y: auto; }
  &__state { display: flex; min-height: 320px; align-items: center; justify-content: center; gap: $ui-space-16; }
  &__state--error { color: $ui-red; }

  &__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-20; }
  &__grid--overview { grid-template-columns: minmax(320px, .85fr) minmax(420px, 1.15fr); }

  &__facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; margin: 0; }
  &__facts div { min-width: 0; padding-bottom: $ui-space-12; border-bottom: 1px solid $ui-line; }
  &__facts dt { margin-bottom: $ui-space-4; color: $ui-muted; font-size: $ui-font-size-metadata; }
  &__facts dd { margin: 0; color: $ui-ink; font-weight: 600; line-height: 1.45; overflow-wrap: anywhere; }
  &__facts a { color: $ui-blue; }

  &__pill { display: inline-flex; padding: 3px 8px; border-radius: 999px; color: #fff; font-size: $ui-font-size-metadata; }
  &__note { margin-top: $ui-space-20; padding: $ui-space-16; border-radius: $ui-control-radius; background: $ui-surface-soft; }
  &__note p { margin: $ui-space-8 0 0; line-height: 1.5; white-space: pre-wrap; }

  &__list { margin: 0; padding: 0; list-style: none; }
  &__list li { padding: $ui-space-14 $ui-space-20; border-top: 1px solid $ui-line; }
  &__list li:first-child { border-top: 0; }
  &__list strong { display: block; color: $ui-ink; line-height: 1.4; }
  &__list span { display: block; margin-top: $ui-space-4; color: $ui-muted; font-size: $ui-font-size-metadata; line-height: 1.45; }
  &__empty { margin: 0; padding: $ui-space-32; color: $ui-muted; text-align: center; }
}

.climbing-preview .app-card--padding-none .app-card__header {
  margin: 0;
  padding: $ui-space-20;
  border-bottom: 1px solid $ui-line;
}

@media (max-width: 900px) {
  .climbing-preview__grid,
  .climbing-preview__grid--overview { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .climbing-preview__toolbar { align-items: flex-start; padding: $ui-space-12 $ui-space-16; flex-direction: column; }
  .climbing-preview__body { padding: $ui-space-16; }
  .climbing-preview__facts { grid-template-columns: 1fr; }
}
</style>
